import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  Award, Filter, X, Search, RefreshCw, Loader2, ArrowUp, ArrowDown,
} from "lucide-react";

interface QTicket {
  id: string;
  tid: string;
  whmcsTicketId: number;
  subject: string;
  admin: string | null;
  respondingAdmins: string | null;
  department: string | null;
  status: string;
  qualityScore: number | null;
  qualityGrade: string | null;
  firstResponseMinutes: number | null;
  replyCount: number | null;
  avgResponseMinutes: number | null;
  resolutionMinutes: number | null;
  departmentTransfers: number;
  reopenCount: number;
  createdAt: string;
  hasFollowUp?: boolean;
  sentimentScore?: number | null;
  sentimentLabel?: string | null;
  sentimentReason?: string | null;
  sentimentAdvice?: string | null;
  ticketType?: string | null;
  workLevel?: string | null;
  workPoints?: number | null;
  sentimentAnalyzedAt?: string | null;
  closedAt?: string | null;
  totalTimeMinutes?: number;
}

function fmtHm(mins: number): string {
  if (!mins || mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

function fmtElapsed(fromIso: string, toIso: string | null | undefined, nowMs: number): string {
  const from = new Date(fromIso).getTime();
  if (isNaN(from) || from > nowMs + 86400_000) return "—"; // reject invalid / future
  const end = toIso ? new Date(toIso).getTime() : nowMs;
  const m = Math.max(0, Math.round((end - from) / 60000));
  return fmtMinutes(m);
}

const SENTIMENT_META: Record<string, { emoji: string; color: string; bg: string; label: string }> = {
  positive: { emoji: "😊", color: "text-green-700",  bg: "bg-green-50 border-green-200",   label: "Positive" },
  neutral:  { emoji: "😐", color: "text-gray-600",   bg: "bg-gray-50 border-gray-200",     label: "Neutral" },
  negative: { emoji: "😞", color: "text-orange-700", bg: "bg-orange-50 border-orange-200", label: "Negative" },
  angry:    { emoji: "😠", color: "text-red-700",    bg: "bg-red-50 border-red-200",       label: "Angry" },
};

const LEVEL_META: Record<string, { color: string; bg: string; label: string; emoji: string }> = {
  simple:   { color: "text-green-700",  bg: "bg-green-50 border-green-200",   label: "Simple",   emoji: "🟢" },
  normal:   { color: "text-yellow-700", bg: "bg-yellow-50 border-yellow-200", label: "Normal",   emoji: "🟡" },
  critical: { color: "text-red-700",    bg: "bg-red-50 border-red-200",       label: "Critical", emoji: "🔴" },
};

const GRADE_META: Record<string, { color: string; bg: string; label: string; emoji: string }> = {
  excellent: { color: "text-green-700", bg: "bg-green-50 border-green-200", label: "Excellent", emoji: "🟢" },
  good:      { color: "text-blue-700",  bg: "bg-blue-50 border-blue-200",   label: "Good",      emoji: "🔵" },
  fair:      { color: "text-amber-700", bg: "bg-amber-50 border-amber-200", label: "Fair",      emoji: "🟡" },
  poor:      { color: "text-red-700",   bg: "bg-red-50 border-red-200",     label: "Poor",      emoji: "🔴" },
};

function fmtMinutes(m: number | null): string {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h < 24) return min ? `${h}h ${min}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

// Quality thresholds — red once value crosses these; green otherwise; gray if null.
const THRESHOLDS = {
  firstReplyMin: 15,
  avgReplyMin: 30,
  replies: 6,
  ageMin: 24 * 60,
};

function thresholdColor(value: number | null | undefined, limit: number): string {
  if (value === null || value === undefined) return "text-gray-400";
  return value > limit ? "text-red-600 font-semibold" : "text-green-600 font-medium";
}

function elapsedMinutes(fromIso: string, toIso: string | null | undefined, nowMs: number): number | null {
  const from = new Date(fromIso).getTime();
  if (isNaN(from) || from > nowMs + 86400_000) return null;
  const end = toIso ? new Date(toIso).getTime() : nowMs;
  return Math.max(0, Math.round((end - from) / 60000));
}

export default function WhmcsQuality() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const isManagerOrAdmin = isAdmin || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";
  const [classifyTicket, setClassifyTicket] = useState<QTicket | null>(null);
  const [ticketTypes, setTicketTypes] = useState<Array<{name:string;level?:string;points?:number}>>([]);

  const shuffledTypes = useMemo(() => {
    const arr = [...ticketTypes];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [ticketTypes, classifyTicket?.id]);
  const [savingClass, setSavingClass] = useState(false);
  useEffect(() => {
    api.get("/whmcs/ticket-types").then(r => setTicketTypes(r.data || [])).catch(() => setTicketTypes([]));
  }, []);
  const submitClassify = async (typeName: string) => {
    if (!classifyTicket) return;
    setSavingClass(true);
    try {
      await api.put(`/whmcs/tickets/${classifyTicket.id}/classify`, { ticketType: typeName });
      toast.success("Classified");
      setClassifyTicket(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setSavingClass(false); }
  };
  const [rows, setRows] = useState<QTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [pendingSentiment, setPendingSentiment] = useState<number>(0);
  const [sentimentDetail, setSentimentDetail] = useState<QTicket | null>(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [timeLogTicket, setTimeLogTicket] = useState<QTicket | null>(null);
  const [timeLogs, setTimeLogs] = useState<any[]>([]);
  const [logHours, setLogHours] = useState<string>("");
  const [logMinutes, setLogMinutes] = useState<string>("");
  const [logComment, setLogComment] = useState<string>("");
  const [savingLog, setSavingLog] = useState(false);

  useEffect(() => {
    if (!timeLogTicket) { setTimeLogs([]); return; }
    api.get(`/whmcs/tickets/${timeLogTicket.id}/time-logs`)
      .then(r => setTimeLogs(r.data || []))
      .catch(() => setTimeLogs([]));
  }, [timeLogTicket]);

  const submitTimeLog = async () => {
    if (!timeLogTicket) return;
    const h = parseInt(logHours || "0", 10) || 0;
    const m = parseInt(logMinutes || "0", 10) || 0;
    const total = h * 60 + m;
    if (total <= 0) { toast.error("Enter hours or minutes"); return; }
    setSavingLog(true);
    try {
      await api.post(`/whmcs/tickets/${timeLogTicket.id}/time-logs`, { minutes: total, comment: logComment.trim() || undefined });
      toast.success(`Logged ${fmtMinutes(total)}`);
      setLogHours(""); setLogMinutes(""); setLogComment("");
      const r = await api.get(`/whmcs/tickets/${timeLogTicket.id}/time-logs`);
      setTimeLogs(r.data || []);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setSavingLog(false); }
  };

  const deleteLog = async (logId: string) => {
    if (!window.confirm("Delete this time entry?")) return;
    try {
      await api.post(`/whmcs/time-logs/${logId}/delete`);
      setTimeLogs(logs => logs.filter((l: any) => l.id !== logId));
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const loadPendingCount = () => {
    api.get("/ai/pending-sentiment-count")
      .then(r => setPendingSentiment(r.data?.count ?? 0))
      .catch(() => setPendingSentiment(0));
  };
  useEffect(() => { loadPendingCount(); }, []);

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortDir, setSortDir] = useState<"asc"|"desc">("desc");
  const [page, setPage] = useState(1);
  const limit = 25;

  const load = () => {
    setLoading(true);
    const params: any = { page, limit, sortBy, sortDir };
    if (search.trim()) params.search = search.trim();
    if (deptFilter) params.department = deptFilter;
    if (gradeFilter) params.grade = gradeFilter;
    if (adminFilter) params.admin = adminFilter;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    api.get("/whmcs/tickets", { params })
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, deptFilter, gradeFilter, adminFilter, dateFrom, dateTo, sortBy, sortDir]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const filtered = rows; // server-side filtered now

  const toggleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };
  const SortIcon = ({ col }: { col: string }) =>
    sortBy !== col
      ? <span className="opacity-30">↕</span>
      : (sortDir === "desc" ? <ArrowDown size={11} className="inline"/> : <ArrowUp size={11} className="inline"/>);

  // Stats
  const stats = useMemo(() => {
    const analyzed = rows.filter(r => r.qualityGrade);
    const counts = { excellent: 0, good: 0, fair: 0, poor: 0 };
    let scoreSum = 0;
    analyzed.forEach(r => {
      if (r.qualityGrade && counts[r.qualityGrade as keyof typeof counts] !== undefined) {
        counts[r.qualityGrade as keyof typeof counts]++;
      }
      scoreSum += r.qualityScore || 0;
    });
    // Per-department ticket counts
    const deptCounts: Record<string, number> = {};
    rows.forEach(r => {
      const key = r.department || "Unassigned";
      deptCounts[key] = (deptCounts[key] || 0) + 1;
    });
    const depts = Object.entries(deptCounts).sort((a, b) => b[1] - a[1]);
    return {
      total: rows.length,
      analyzed: analyzed.length,
      avgScore: analyzed.length > 0 ? Math.round(scoreSum / analyzed.length * 10) / 10 : 0,
      counts,
      depts,
    };
  }, [rows]);

  const distinctDepts = Array.from(new Set(rows.map(r => r.department).filter(Boolean))).sort();
  const distinctAdmins = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      const names = (r.respondingAdmins || r.admin || "").split(",").map(s => s.trim()).filter(Boolean);
      names.forEach(n => set.add(n));
    });
    return Array.from(set).sort();
  }, [rows]);
  const hasFilters = search || gradeFilter || deptFilter || adminFilter || dateFrom || dateTo;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const resetFilters = () => {
    setSearch(""); setGradeFilter(""); setDeptFilter(""); setAdminFilter("");
    setDateFrom(""); setDateTo(""); setSortBy("updatedAt"); setSortDir("desc");
    setPage(1);
  };

  const recomputeAll = async () => {
    if (!window.confirm("Recompute quality for all stored tickets? This may take a while.")) return;
    setRecomputing(true);
    try {
      const r = await api.post("/whmcs/recompute-all-quality");
      toast.success(`Processed ${r.data.processed} of ${r.data.total} tickets`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Recompute failed");
    } finally { setRecomputing(false); }
  };

  return (
    <div className="w-full max-w-none p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Award className="text-purple-500" size={26}/>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">WHMCS Quality</h1>
            <p className="text-sm text-gray-500">Automated quality analysis based on 8 metrics</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
              if (pendingSentiment === 0) {
                toast.success("All tickets already analyzed ✨");
                return;
              }
              if (!window.confirm(`Analyze sentiment for ${pendingSentiment} new ticket(s)? Already-analyzed tickets are skipped.`)) return;
              setRecomputing(true);
              try {
                const r = await api.post("/ai/analyze-all-sentiments");
                const rl = r.data.rateLimited ? ` · ${r.data.rateLimited} rate-limited` : "";
                toast.success(`Analyzed ${r.data.analyzed} / ${r.data.total} · ${r.data.errors} errors${rl}`);
                loadPendingCount();
                load();
              } catch (err: any) {
                toast.error(err?.response?.data?.message || "Analysis failed");
              } finally { setRecomputing(false); }
            }}
            disabled={recomputing}
            title={pendingSentiment === 0 ? "No new tickets to analyze" : `${pendingSentiment} ticket(s) pending`}
            className={`flex items-center gap-2 border px-3 py-2 rounded-lg text-sm ${
              pendingSentiment === 0
                ? "border-gray-200 text-gray-400 cursor-default"
                : "border-purple-300 text-purple-700 hover:bg-purple-50"
            }`}>
            {recomputing ? <Loader2 size={14} className="animate-spin"/> : <span>🤖</span>}
            {pendingSentiment === 0
              ? "Sentiment ✓ up to date"
              : `Analyze New (${pendingSentiment})`}
          </button>
          <button onClick={recomputeAll} disabled={recomputing}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">
            {recomputing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            Recompute All
          </button>
        </div>
      </div>

      {/* Overview + Departments — all in one row */}
      <div className="flex gap-3 mb-5 overflow-x-auto pb-1">
        <div className="flex-1 min-w-[130px] bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Analyzed</div>
          <div className="text-2xl font-bold text-gray-800">{stats.analyzed}<span className="text-sm text-gray-400"> / {stats.total}</span></div>
        </div>
        {stats.depts.map(([name, count]) => {
          const active = deptFilter === name;
          return (
            <div key={name}
              onClick={() => setDeptFilter(active ? "" : name)}
              className={`flex-1 min-w-[130px] bg-white rounded-xl border p-4 text-center cursor-pointer transition-all ${
                active ? "ring-2 ring-offset-1 ring-purple-400 border-purple-300" : "border-gray-200 hover:border-gray-300"
              }`}>
              <div className="text-xs text-gray-500 truncate" title={name}>{name}</div>
              <div className="text-2xl font-bold text-gray-800">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
          <Filter size={13}/> Filters
          {hasFilters && (
            <button onClick={resetFilters}
              className="ml-auto flex items-center gap-1 text-red-500 hover:text-red-700 font-normal">
              <X size={11}/> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full pl-9 border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Departments</option>
            {distinctDepts.map(d => <option key={d!} value={d!}>{d}</option>)}
          </select>
          <select value={adminFilter} onChange={e => setAdminFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Admins</option>
            {distinctAdmins.map(a => <option key={a!} value={a!}>{a}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">From date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">To date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Sort by</label>
            <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="updatedAt">Last update</option>
              <option value="createdAt">Created</option>
              <option value="score">Quality Score</option>
              <option value="grade">Grade</option>
              <option value="firstReply">First Response</option>
              <option value="avgReply">Avg Response</option>
              <option value="replies">Replies count</option>
              <option value="resolution">Age (open time)</option>
              <option value="transfers">Transfers</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Direction</label>
            <select value={sortDir} onChange={e => { setSortDir(e.target.value as any); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="desc">Highest → Lowest</option>
              <option value="asc">Lowest → Highest</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 bg-gray-50 rounded-xl text-center text-gray-400">
          No tickets match filters. Open a ticket to compute its quality, or click "Recompute All".
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  { label: "TID", key: "tid" },
                  { label: "Subject", key: "subject" },
                  { label: "Admin", key: "admin" },
                  { label: "Dept", key: "dept" },
                  { label: "1st Reply", key: "firstReply" },
                  { label: "Avg Reply", key: "avgReply" },
                  { label: "Replies", key: "replies" },
                  { label: "Age", key: "resolution" },
                  { label: "Time", key: "time" },
                  { label: "Transfers", key: "transfers" },
                  { label: "Follow-up", key: "followUp" },
                  { label: "Sentiment", key: "sentiment" },
                ].map(h => (
                  <th key={h.key} onClick={() => toggleSort(h.key)}
                    className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:text-gray-800 select-none">
                    {h.label} <SortIcon col={h.key}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(t => {
                return (
                  <tr key={t.id} onClick={() => window.open(`/whmcs-tickets/${t.id}`, "_blank", "noopener")}
                    className="hover:bg-purple-50 cursor-pointer">
                    <td className="px-3 py-3 font-mono text-xs text-gray-500">{t.tid || t.whmcsTicketId}</td>
                    <td className="px-3 py-3 max-w-xs truncate text-gray-800">{t.subject}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs">{t.respondingAdmins || t.admin || "—"}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{t.department || "—"}</td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${thresholdColor(t.firstResponseMinutes, THRESHOLDS.firstReplyMin)}`}>{fmtMinutes(t.firstResponseMinutes)}</td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${thresholdColor(t.avgResponseMinutes, THRESHOLDS.avgReplyMin)}`}>{fmtMinutes(t.avgResponseMinutes)}</td>
                    <td className={`px-3 py-3 text-xs text-center ${thresholdColor(t.replyCount, THRESHOLDS.replies)}`}>{t.replyCount ?? "—"}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">
                      {(() => {
                        const isClosed = (t.status || "").toLowerCase().includes("closed");
                        const endIso = isClosed ? (t.closedAt || null) : null;
                        const ageMin = elapsedMinutes(t.createdAt, endIso, nowMs);
                        return (
                          <span className={`inline-flex items-center gap-1.5 ${thresholdColor(ageMin, THRESHOLDS.ageMin)}`} title={isClosed ? "Closed" : "Open"}>
                            <span className={`inline-block w-2 h-2 rounded-full ${isClosed ? "bg-gray-400" : "bg-emerald-500 animate-pulse"}`}/>
                            <span>{fmtElapsed(t.createdAt, endIso, nowMs)}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setTimeLogTicket(t); }}
                        title="Log time on this ticket"
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${
                          (t.totalTimeMinutes ?? 0) > 0
                            ? "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                            : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                        }`}>
                        ⏱ {fmtHm(t.totalTimeMinutes ?? 0)}
                      </button>
                    </td>
                    <td className={`px-3 py-3 text-xs text-center ${t.departmentTransfers > 0 ? "text-red-600 font-medium" : "text-gray-400"}`}>{t.departmentTransfers}</td>
                    <td className="px-3 py-3 text-xs text-center">
                      {t.hasFollowUp ? <span title="Admin followed up after last client message" className="text-green-600">✓</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <button type="button" onClick={(e) => { e.stopPropagation(); setClassifyTicket(t); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium hover:brightness-95 ${
                          t.ticketType
                            ? (t.workLevel && LEVEL_META[t.workLevel] && (isManagerOrAdmin))
                              ? `${LEVEL_META[t.workLevel].bg} ${LEVEL_META[t.workLevel].color}`
                              : "bg-indigo-50 border-indigo-200 text-indigo-700"
                            : "bg-gray-50 border-gray-200 text-gray-500"
                        }`}
                        title={t.ticketType || "Classify this ticket"}>
                        {t.ticketType ? (
                          <>
                            {isManagerOrAdmin && t.workLevel && LEVEL_META[t.workLevel] && <span>{LEVEL_META[t.workLevel].emoji}</span>}
                            <span className="truncate max-w-[140px]">{t.ticketType}</span>
                            {isManagerOrAdmin && t.workPoints != null && <span className="text-[10px] opacity-70">·{t.workPoints}pt</span>}
                          </>
                        ) : (
                          <><span>🏷️</span><em>Classify…</em></>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-xs text-center">
                      {t.sentimentLabel && SENTIMENT_META[t.sentimentLabel] ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSentimentDetail(t); }}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium hover:brightness-95 hover:scale-105 transition-transform cursor-pointer ${SENTIMENT_META[t.sentimentLabel].bg} ${SENTIMENT_META[t.sentimentLabel].color}`}
                          title="Click for reason + recommended action">
                          <span className="text-base leading-none">{SENTIMENT_META[t.sentimentLabel].emoji}</span>
                          <span>{SENTIMENT_META[t.sentimentLabel].label}</span>
                        </button>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
              <div>Page {page} of {totalPages}</div>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="border border-gray-300 rounded px-3 py-1 hover:bg-gray-50 disabled:opacity-40">Previous</button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="border border-gray-300 rounded px-3 py-1 hover:bg-gray-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {timeLogTicket && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setTimeLogTicket(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-indigo-50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-indigo-800">⏱ Time on Ticket #{timeLogTicket.tid}</div>
                  <div className="text-xs text-indigo-600 mt-1">
                    Total: {fmtHm(timeLogs.reduce((s: number, l: any) => s + (l.minutes || 0), 0))}
                  </div>
                </div>
                <button onClick={() => setTimeLogTicket(null)} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Log new time</div>
                <div className="flex items-end gap-2">
                  <div className="w-20">
                    <label className="block text-[10px] text-gray-500 mb-0.5">Hours</label>
                    <input type="number" min="0" value={logHours} onChange={e => setLogHours(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"/>
                  </div>
                  <div className="w-20">
                    <label className="block text-[10px] text-gray-500 mb-0.5">Minutes</label>
                    <input type="number" min="0" max="59" value={logMinutes} onChange={e => setLogMinutes(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"/>
                  </div>
                  <input value={logComment} onChange={e => setLogComment(e.target.value)} placeholder="Note (optional)…"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"/>
                  <button onClick={submitTimeLog} disabled={savingLog}
                    className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                    {savingLog ? "…" : "Log"}
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Entries ({timeLogs.length})</div>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {timeLogs.length === 0 && <div className="text-sm text-gray-400 text-center py-4">No time logged yet</div>}
                  {timeLogs.map((log: any) => (
                    <div key={log.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs">
                      <span className="font-semibold text-indigo-700 min-w-[60px]">{fmtHm(log.minutes)}</span>
                      <span className="text-gray-700 flex-1">
                        <span className="font-medium">{log.employee?.name}</span>
                        {log.comment && <span className="text-gray-500 ml-1">· {log.comment}</span>}
                      </span>
                      <span className="text-gray-400">{new Date(log.date).toLocaleDateString("en-GB", {day:"2-digit",month:"short"})}</span>
                      <button onClick={() => deleteLog(log.id)} className="text-red-500 hover:text-red-700"><X size={12}/></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-gray-400 text-center pt-2 border-t border-gray-100">
                💡 Logged time also appears in your Timesheet & Reports
              </div>
            </div>
          </div>
        </div>
      )}

      {classifyTicket && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setClassifyTicket(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-indigo-50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-indigo-800">🏷️ Classify Ticket #{classifyTicket.tid}</div>
                  <div className="text-xs text-indigo-600 mt-1 truncate max-w-md">{classifyTicket.subject}</div>
                </div>
                <button onClick={() => setClassifyTicket(null)} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
              </div>
            </div>
            <div className="p-5 space-y-2 max-h-[70vh] overflow-y-auto">
              {isManagerOrAdmin ? (
                (["simple","normal","critical"] as const).map(lvl => {
                  const items = ticketTypes.filter((t:any) => t.level === lvl);
                  const meta = LEVEL_META[lvl];
                  if (!items.length) return null;
                  return (
                    <div key={lvl}>
                      <div className={`text-xs font-semibold uppercase px-2 py-1 rounded-md mb-1 ${meta.bg} ${meta.color}`}>
                        {meta.emoji} {meta.label} · {items[0].points}pt
                      </div>
                      <div className="grid grid-cols-1 gap-1 mb-2">
                        {items.map(t => (
                          <button key={t.name} disabled={savingClass}
                            onClick={() => submitClassify(t.name)}
                            className={`text-left px-3 py-2 text-sm rounded-md border transition-all hover:bg-indigo-50 hover:border-indigo-300 ${
                              classifyTicket.ticketType === t.name ? "bg-indigo-100 border-indigo-400 font-semibold" : "bg-white border-gray-200"
                            }`}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="grid grid-cols-1 gap-1">
                  {shuffledTypes.map(t => (
                    <button key={t.name} disabled={savingClass}
                      onClick={() => submitClassify(t.name)}
                      className={`text-left px-3 py-2 text-sm rounded-md border transition-all hover:bg-indigo-50 hover:border-indigo-300 ${
                        classifyTicket.ticketType === t.name ? "bg-indigo-100 border-indigo-400 font-semibold" : "bg-white border-gray-200"
                      }`}>
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
              {!isManagerOrAdmin && classifyTicket.ticketType && (
                <div className="text-[11px] text-gray-500 text-center pt-2 border-t border-gray-100">
                  ℹ️ Already classified. Only Manager can change.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {sentimentDetail && sentimentDetail.sentimentLabel && SENTIMENT_META[sentimentDetail.sentimentLabel] && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setSentimentDetail(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-4 border-b ${SENTIMENT_META[sentimentDetail.sentimentLabel].bg}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{SENTIMENT_META[sentimentDetail.sentimentLabel].emoji}</span>
                  <div>
                    <div className={`text-lg font-semibold ${SENTIMENT_META[sentimentDetail.sentimentLabel].color}`}>
                      {SENTIMENT_META[sentimentDetail.sentimentLabel].label}
                    </div>
                    <div className="text-xs text-gray-600">
                      Ticket #{sentimentDetail.tid} · Score {sentimentDetail.sentimentScore?.toFixed(2) ?? "—"}
                    </div>
                  </div>
                </div>
                <button onClick={() => setSentimentDetail(null)}
                  className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
              </div>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-1.5">💭 Why this rating</div>
                <div className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 leading-relaxed" dir="auto">
                  {sentimentDetail.sentimentReason || <span className="text-gray-400">No reason provided</span>}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-1.5">🎯 Recommended action</div>
                <div className="text-sm text-gray-800 bg-purple-50 border border-purple-100 rounded-lg p-3 leading-relaxed" dir="auto">
                  {sentimentDetail.sentimentAdvice
                    || <span className="text-gray-400">No advice available. Re-analyze this ticket to generate recommendations.</span>}
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-400">
                  {sentimentDetail.sentimentAnalyzedAt
                    ? `Analyzed ${new Date(sentimentDetail.sentimentAnalyzedAt).toLocaleString()}`
                    : ""}
                </div>
                <button
                  disabled={analyzingId === sentimentDetail.id}
                  onClick={async () => {
                    setAnalyzingId(sentimentDetail.id);
                    try {
                      const r = await api.post(`/ai/analyze-sentiment/${sentimentDetail.id}`);
                      toast.success("Re-analyzed");
                      setSentimentDetail({
                        ...sentimentDetail,
                        sentimentLabel: r.data.label,
                        sentimentScore: r.data.score,
                        sentimentReason: r.data.reason,
                        sentimentAdvice: r.data.advice,
                        sentimentAnalyzedAt: new Date().toISOString(),
                      });
                      load();
                    } catch (err: any) {
                      toast.error(err?.response?.data?.message || "Failed to re-analyze");
                    } finally { setAnalyzingId(null); }
                  }}
                  className="flex items-center gap-1.5 text-xs bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 disabled:opacity-50">
                  {analyzingId === sentimentDetail.id
                    ? <><Loader2 size={12} className="animate-spin"/> Analyzing...</>
                    : <>🔄 Re-analyze</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
